import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';

declare global {
  interface Window {
    Sentry?: typeof Sentry;
  }
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
let sentryInitialized = false;

function maskDsn(dsn: string) {
  try {
    const url = new URL(dsn);
    return `${url.protocol}//${url.hostname}${url.pathname}`;
  } catch {
    return '[invalid-dsn-format]';
  }
}

export function initSentry() {
  if (import.meta.env.DEV) {
      window.Sentry = Sentry;
  }

  console.info('[NxtRev:Sentry] init requested', {
    mode: import.meta.env.MODE,
    prod: import.meta.env.PROD,
    hasDsn: Boolean(sentryDsn),
  });

  if (sentryInitialized) {
    console.info('[NxtRev:Sentry] already initialized');
    return;
  }

  if (!import.meta.env.PROD || !sentryDsn) {
    return;
  }

  if (!sentryDsn) {
    console.warn('[NxtRev:Sentry] skipped: VITE_SENTRY_DSN is missing');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });

  sentryInitialized = true;
  console.info('[NxtRev:Sentry] initialized', {
    environment: import.meta.env.MODE,
    dsn: maskDsn(sentryDsn),
  });
}

export function SentryErrorFallback(): ReactNode {
  return (
    <main className="min-h-screen bg-white text-black flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
        <p className="text-sm text-gray-600">
          We have been notified and are already looking into it.
        </p>
      </div>
    </main>
  );
}

export function SentryTestError() {
  if (new URLSearchParams(window.location.search).has('sentry-test')) {
    throw new Error('NxtRev Sentry test error');
  }

  return null;
}

export { Sentry };